import { useCallback, useEffect, useState } from "react";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useUpdateUser } from "@apis/user";
import { ToastTypes, showToast } from "@components/molecules/VDBToast/util";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useTheme } from "styled-components/native";
import { useNavigation } from "@react-navigation/native";

export type CurrencyParams = {
  currency_symbol: string;
  label: string;
  value: string;
};

const useCurrencySelection = (navigationProp?: any) => {
  const userInfo = useUserInfo();
  const { t } = useTranslation();
  const { mutate, isLoading } = useUpdateUser(userInfo.getUserId());
  const [currencies, setCurrencies] = useState<CurrencyParams[]>([]);
  const [selectedCurrency, setSelectedCurrency] =
    useState<CurrencyParams | null>(null);
  const installationIdentifier = useSelector(
    (state: any) => state.appState?.installationIdentifier
  );
  const theme = useTheme();

  let defaultNavigation;
  try {
    defaultNavigation = useNavigation();
  } catch (error) {
    defaultNavigation = null;
  }
  const navigation =
    navigationProp !== undefined ? navigationProp : defaultNavigation;

  useEffect(() => {
    if (!theme.isMobile && navigation) {
      navigation.setOptions({
        headerLeft: () => <></>,
      });
    }

    if (
      userInfo.user?.feature_settings?.currency_conversion &&
      currencies.length === 0 &&
      userInfo.user?.user_settings?.currency_list
    )
      setCurrencies(userInfo.user?.user_settings?.currency_list || []);

    if (!selectedCurrency && userInfo.user?.currency && currencies.length > 0) {
      const userCurrency = userInfo.user?.currency;
      const defaultCurrency = currencies.find((c) => c.value === userCurrency);
      setSelectedCurrency(defaultCurrency as CurrencyParams);
    }
  }, [userInfo, currencies]);

  const changeCurrency = useCallback(
    (currencyId: string) => {
      const currency = currencies.find(
        (_currency) => _currency.value === currencyId
      );

      if (!currency) return;
      setSelectedCurrency(currency);

      mutate(
        {
          user: {
            currency: currency.value,
            installation_identifier: installationIdentifier,
          },
        },
        {
          onSuccess(data: any) {
            showToast(
              t("currency.toast.title.text"),
              t(data.data?.translation_key ?? data.data?.message),
              ToastTypes.SUCCESS,
              true,
              true
            );
          },
        }
      );
    },
    [currencies]
  );

  return { selectedCurrency, currencies, changeCurrency, isLoading };
};

export default useCurrencySelection;
